import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trash2, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Helper functions
function formatMoney(cents?: number) {
  if (cents === undefined || cents === null) return "0.00 €";
  return (cents / 100).toFixed(2) + " €";
}

function formatTax(bp?: number) {
  if (typeof bp !== "number") return "";
  return (bp / 100).toFixed(2) + " %";
}

function centsToValue(c?: number) {
  if (typeof c !== "number") return "";
  return (c / 100).toFixed(2);
}

function valueToCents(v: string) {
  const num = parseFloat(v.replace(",", "."));
  if (Number.isNaN(num)) return 0;
  return Math.round(num * 100);
}

export interface TaskItem {
  id?: number;
  name: string;
  description?: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  vatRate?: number; // percent
  priceInputMode?: "with_vat" | "without_vat";
}

interface TaskItemsFormProps {
  items: TaskItem[]; // len pre fallback ak nemáme taskId
  onChange: (items: TaskItem[]) => void; // prázdna funkcia
  taskId?: number;
  isVatPayer?: boolean;
  companyVatRate?: number;
}

export function TaskItemsForm({ items: fallbackItems, onChange, taskId, isVatPayer = false, companyVatRate }: TaskItemsFormProps) {
  const round4 = (value: number) => Math.round((value + Number.EPSILON) * 10000) / 10000;
  const toNumber = (value: unknown) => {
    const normalized = String(value ?? "").replace(",", ".");
    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const format4 = (value: number) => round4(value).toFixed(4);
  const vatPercent = (value: unknown) => {
    const numeric = toNumber(value);
    return numeric <= 1 ? numeric * 100 : numeric;
  };
  // Default DPH pre platcu DPH má byť vždy 23% (aj keď profil má iný companyVatRate).
  const vatDefaultPercent = 23;

  // Načítanie aktuálnych items z API ak máme taskId
  const { data: apiItems = [], isLoading } = useQuery<TaskItem[]>({
    queryKey: ['/api/tasks', taskId, 'items'],
    queryFn: async () => {
      const res = await apiRequest('GET', `/api/tasks/${taskId}/items`);
      return res.json();
    },
    enabled: !!taskId
  });
  
  // Lokálny stav pre editing
  const [localItems, setLocalItems] = useState<TaskItem[]>([]);
  
  // Synchronizácia s API dátami
  useEffect(() => {
    if (taskId && apiItems.length >= 0) {
      // Ak je používateľ platca DPH, predvolený DPH v UI má byť 23%.
      // Legacy položky môžu mať v DB 19%, takže ich UI normalizujeme na 23%.
      const normalized = isVatPayer
        ? apiItems.map((it) => {
            const currentVatPercent = vatPercent(it.vatRate);
            if (it.vatRate == null || currentVatPercent === 19) {
              return { ...it, vatRate: vatDefaultPercent };
            }
            return it;
          })
        : apiItems;

      setLocalItems([...normalized]); // vytvoríme novú kópiu
      onChange([...normalized]); // synchronizujeme s rodičom
    } else if (!taskId && fallbackItems.length >= 0) {
      setLocalItems([...fallbackItems]); // vytvoríme novú kópiu
      onChange([...fallbackItems]); // synchronizujeme s rodičom
    }
  }, [apiItems, fallbackItems, taskId, isVatPayer]); // synchronizácia s props
  
  // Použijeme lokálny stav pre editing
  const items = localItems;
  const { t } = useTranslation();
  const { toast } = useToast();
  
  // Mutácia pre vytvorenie novej položky
  const createItemMutation = useMutation({
    mutationFn: async (newItem: TaskItem) => {
      if (!taskId) throw new Error('Task ID is required');
      const res = await apiRequest('POST', `/api/tasks/${taskId}/items`, newItem);
      return res.json();
    },
    onSuccess: (newItem) => {
      // Nahradíme optimistic item s real item z servera
      queryClient.setQueryData(['/api/tasks', taskId, 'items'], (oldData: TaskItem[] = []) => {
        // Odstránime optimistic item (s dočasným ID) a pridáme real item
        const filteredData = oldData.filter(item => !item.id || item.id < 1000000000000); // filter optimistic items
        return [...filteredData, newItem];
      });
      
      // Invalidácia ostatných cache
      queryClient.invalidateQueries({ queryKey: ['/api/tasks', taskId] });
      queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
      queryClient.invalidateQueries({ queryKey: ['/api/tasks/statistics'] });
      
      toast({
        title: t('Položka bola vytvorená'),
        description: t('Nová položka bola úspešne pridaná'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('Chyba pri vytváraní položky'),
        description: error.message,
        variant: 'destructive',
      });
    }
  });

  const addItem = () => {
    if (!taskId) {
      // Ak nemáme taskId, pridáme len lokálne (fallback)
      const newItem: TaskItem = {
        name: "",
        description: "",
        quantity: 1,
        unitPrice: 0,
        amount: 0,
      };
      onChange([...items, newItem]);
      return;
    }

    // Vytvoríme položku s aspoň nejakými základnými údajmi
    const newItem: TaskItem = {
      name: "Nová položka",
      description: "",
      quantity: 1,
      unitPrice: 0,
      amount: 0,
      vatRate: isVatPayer ? vatDefaultPercent : 0,
      priceInputMode: "without_vat",
    };
    
    // OPTIMISTIC UPDATE - pridáme položku okamžite do lokálneho stavu
    const tempId = Date.now(); // dočasné ID
    const optimisticItem = { ...newItem, id: tempId };
    
    // Okamžite aktualizujeme lokálny stav
    console.log('[TASKITEMSFORM] Adding optimistic item with tempId:', tempId);
    setLocalItems(prev => [...prev, optimisticItem]);
    
    // Synchronizujeme s rodičom
    onChange([...localItems, optimisticItem]);
    
    // Zavoláme API
    createItemMutation.mutate(newItem);
  };

  // Mutácia pre aktualizáciu existujúcej položky
  const updateItemMutation = useMutation({
    mutationFn: async ({ itemId, updatedItem }: { itemId: number, updatedItem: TaskItem }) => {
      const res = await apiRequest('PATCH', `/api/task-items/${itemId}`, updatedItem);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/tasks', taskId, 'items'] });
      queryClient.invalidateQueries({ queryKey: ['/api/tasks', taskId] });
    },
    onError: (error: Error) => {
      toast({
        title: t('Chyba pri aktualizácii položky'),
        description: error.message,
        variant: 'destructive',
      });
    }
  });

  const updateItem = (index: number, field: keyof TaskItem, value: any) => {
    // Updating item field
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    
    // Automaticky prepočítať sumu pri zmene množstva alebo ceny
    if (field === "quantity" || field === "unitPrice" || field === "priceInputMode" || field === "vatRate") {
      const quantity = toNumber(updatedItems[index].quantity);
      const vatRate = vatPercent(updatedItems[index].vatRate);
      const mode = updatedItems[index].priceInputMode ?? "without_vat";

      if (field === "unitPrice") {
        const enteredPrice = toNumber(value);
        const netUnitPrice = (isVatPayer && mode === "with_vat")
          ? enteredPrice / (1 + vatRate / 100)
          : enteredPrice;
        updatedItems[index].unitPrice = round4(netUnitPrice);
      }

      const unitPrice = toNumber(updatedItems[index].unitPrice);
      updatedItems[index].amount = round4(quantity * unitPrice);
    }
    
    // Setting updated items
    // Okamžite aktualizujeme lokálny stav
    setLocalItems(updatedItems);
    
    // Synchronizujeme s rodičom
    onChange(updatedItems);
    
    // Ak má položka ID (nie je optimistic), uložíme ju do databázy
    const item = updatedItems[index];
    if (item.id && typeof item.id === 'number' && item.id < 1000000000000) { // nie je optimistic item
      updateItemMutation.mutate({ itemId: item.id, updatedItem: item });
    }
  };

  // Mutácia pre zmazanie položky
  const deleteItemMutation = useMutation({
    mutationFn: async (itemId: number) => {
      await apiRequest('DELETE', `/api/task-items/${itemId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/tasks', taskId, 'items'] });
      queryClient.invalidateQueries({ queryKey: ['/api/tasks', taskId] });
      toast({
        title: t('Položka bola zmazaná'),
        description: t('Položka bola úspešne odstránená'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t('Chyba pri mazaní položky'),
        description: error.message,
        variant: 'destructive',
      });
    }
  });

  const removeItem = (index: number) => {
    const item = items[index];
    
    // Okamžite odstránime z lokálneho stavu (optimistic update)
    console.log('[TASKITEMSFORM] Deleting item at index:', index, 'item:', item);
    const updatedItems = items.filter((_, i) => i !== index);
    setLocalItems(updatedItems);
    
    // Synchronizujeme s rodičom
    onChange(updatedItems);
    
    // Ak má položka real ID, zmažeme ju v databáze
    if (item.id && typeof item.id === 'number' && item.id < 1000000000000) { // nie je optimistic item
      deleteItemMutation.mutate(item.id);
    }
  };

  // Calculate total from items
  const totalAmount = Array.isArray(items) ? items.reduce((sum, item) => sum + (item?.amount ?? 0), 0) : 0;

  const VAT_OPTIONS = [0, 5, 19, 20, 23];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">{t("Položky úlohy")}</h3>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addItem}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          {t("Pridať položku")}
        </Button>
      </div>

      <div className="space-y-3">
        {Array.isArray(items) && items.length > 0 ? items
          .filter(item => item && typeof item === 'object' && item !== null)
          .map((item, index) => (
            <Card key={item.id ?? `item-${index}`} className="relative">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">
                  {t("Položka")} {index + 1}
                </CardTitle>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeItem(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Input
                  placeholder={t("Názov položky")}
                  value={item.name ?? ""}
                  onChange={(e) => updateItem(index, "name", e.target.value)}
                />
              </div>
              
              <div>
                <Textarea
                  placeholder={t("Popis položky (voliteľné)")}
                  value={item.description ?? ""}
                  onChange={(e) => updateItem(index, "description", e.target.value)}
                  className="resize-none"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-sm font-medium">{t("Množstvo")}</label>
                  <Input
                    type="number"
                    step="0.0001"
                    min="0"
                    placeholder="1"
                    value={item.quantity ?? 0}
                    onChange={(e) => updateItem(index, "quantity", e.target.value)}
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium">{t("Cena za kus")} (€)</label>
                  <Input
                    type="number"
                    step="0.0001"
                    min="0"
                    placeholder="0.00"
                    value={
                      isVatPayer && (item.priceInputMode ?? "without_vat") === "with_vat"
                        ? format4((toNumber(item.unitPrice) * (1 + vatPercent(item.vatRate ?? 0) / 100)))
                        : (item.unitPrice ?? 0)
                    }
                    onChange={(e) => updateItem(index, "unitPrice", e.target.value)}
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium">{t("Celkom")} (4)</label>
                  <div className="flex items-center h-10 px-3 py-2 border border-input bg-muted rounded-md">
                    <span className="font-medium">{format4(item.amount ?? 0)} €</span>
                  </div>
                </div>
              </div>

              {isVatPayer && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium">Typ ceny</label>
                    <Select
                      value={item.priceInputMode ?? "without_vat"}
                      onValueChange={(v: "with_vat" | "without_vat") => {
                        updateItem(index, "priceInputMode", v);
                      }}
                    >
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="Bez DPH" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="with_vat">S DPH</SelectItem>
                        <SelectItem value="without_vat">Bez DPH</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">DPH (%)</label>
                    <Select
                      value={String(vatPercent(item.vatRate ?? vatDefaultPercent))}
                      onValueChange={(v) => updateItem(index, "vatRate", parseFloat(v))}
                    >
                      <SelectTrigger className="h-10">
                        <SelectValue placeholder="20" />
                      </SelectTrigger>
                      <SelectContent>
                        {VAT_OPTIONS.map((r) => (
                          <SelectItem key={r} value={String(r)}>{r} %</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )) : null}
      </div>

      {Array.isArray(items) && items.length > 0 && (
        <div className="flex justify-end">
          <Badge variant="secondary" className="text-base px-4 py-2">
            {t("Celkom z položiek")}: {format4(totalAmount)} €
          </Badge>
        </div>
      )}

      {(!Array.isArray(items) || items.length === 0) && (
        <div className="text-center py-8 text-muted-foreground">
          <p>{t("Zatiaľ nemáte pridané žiadne položky")}</p>
          <p className="text-sm mt-1">{t("Kliknite na 'Pridať položku' pre začiatok")}</p>
        </div>
      )}
    </div>
  );
}